import { useEffect, useState } from 'react';

import { getCopy } from '../../data/sanity/sanity-helpers';
import type { GetCopyResult } from '../../data/sanity/types';
import { useLogService } from '../../hooks/useLogService';
import { isSSR } from '../../utils/helpers';
import {
  getMarket,
  guessMarketByUrl,
  type SemboMarket,
} from '../../utils/markets';

// This function is a hacky short-term solution to https://trello.com/c/yACAfD5U
// Hoping that we can find a better solution by fixing the underlying problem,
// which I suspect is related to our cache handler in redis throwing out cache,
// followed by nextjs getting confused and returning {} from getStaticProps
export const useHackyReloadIfPagePropsIsUndefined = (
  pageProps: any,
  __N_SSG: boolean | undefined,
  market: SemboMarket | null
): { pagePropsMissing: boolean } => {
  const { logError } = useLogService();

  if (isSSR()) {
    return { pagePropsMissing: false };
  }

  const sessionStorageKey = 'hacky-inspire-next-js-ssg-reload';

  const getStaticPropsMissing =
    Object.keys(pageProps || {}).length === 0 && __N_SSG === undefined;

  if (!getStaticPropsMissing) {
    try {
      sessionStorage.removeItem(sessionStorageKey);
    } catch (e) {
      /* noop */
    }
    return { pagePropsMissing: false };
  }

  if (sessionStorage.getItem(sessionStorageKey) !== 'true' && market) {
    // it's a market-page, so there should always be static props
    // reload the page to try to fetch them again
    try {
      sessionStorage.setItem(sessionStorageKey, 'true');
    } catch (e) {
      /* noop */
    }

    logError(
      new Error(
        `getStaticProps missing for page "${window.location.href}", reloading page once.`
      )
    );
    window.location.reload();
  }

  if (!market) {
    return { pagePropsMissing: false };
  }

  return { pagePropsMissing: true };
};

export const useEssentialProps = (
  pageProps: any,
  options?: {
    forceClientsideMicrocopy?: boolean;
    forceClientsideMacrocopy?: boolean;
    skipFetchingMacrocopy?: boolean;
    fallbackMarket?: SemboMarket;
  }
) => {
  const [market, setMarket] = useState<SemboMarket | null>(pageProps?.market);
  const [microcopy, setMicrocopy] = useState<GetCopyResult | undefined>();
  const [macrocopy, setMacrocopy] = useState<GetCopyResult | undefined>();

  useEffect(() => {
    const fn = async () => {
      if (!pageProps?.market && !market) {
        setMarket(
          (guessMarketByUrl(window.location.href) as SemboMarket) ??
            options?.fallbackMarket
        );
      }

      const microcopyPromise =
        (!pageProps?.microcopy || options?.forceClientsideMicrocopy) &&
        !microcopy &&
        market
          ? getCopy('microcopy', undefined, market, pageProps?.previewSettings)
          : undefined;

      const macrocopyPromise =
        !options?.skipFetchingMacrocopy &&
        (!pageProps?.macrocopy || options?.forceClientsideMacrocopy) &&
        !macrocopy &&
        market
          ? getCopy(
              'macrocopy',
              {
                include: [
                  'explore.amelia.initialMessage',
                  'explore-lofi.maintenance.paragraph',
                ],
              },
              market,
              pageProps?.previewSettings
            )
          : undefined;

      const [newMicrocopy, newMacrocopy] = await Promise.all([
        microcopyPromise,
        macrocopyPromise,
      ]);

      if (newMicrocopy) {
        setMicrocopy(newMicrocopy);
      }

      if (newMacrocopy) {
        setMacrocopy(newMacrocopy);
      }
    };

    fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    market,
    pageProps?.market,
    pageProps?.microcopy,
    pageProps?.macrocopy,
    pageProps?.previewSettings,
    options?.forceClientsideMicrocopy,
    options?.forceClientsideMacrocopy,
    options?.skipFetchingMacrocopy,
  ]);

  return {
    market: market ?? pageProps?.market,
    microcopy: microcopy ?? pageProps?.microcopy,
    macrocopy: macrocopy ?? pageProps?.macrocopy,
  };
};

export const useInternalMarket = () => {
  const [onInternalDomain, setOnInternalDomain] = useState(false);
  useEffect(() => {
    if (
      !getMarket(window.location.host) ||
      window.location.host.includes('localhost')
    ) {
      setOnInternalDomain(true);
    }
  }, []);

  return onInternalDomain;
};
